// @flow
import { createAction, createReducer } from 'redux-act';
import { createSelector } from 'reselect';
import { superpathjoin } from 'superpathjoin';

import { selectCurrentId } from '@gandi/redux-sharing';
import { selectFromConfig } from '@gandi/redux-config';
import type { FetchResponse } from '@gandi/redux-resource/dist/types';
import { localeSelector } from '@gandi/translate';

import { POST } from 'gandi.redux-fetcher';
import {
  actions as formActions,
  mapErrors,
  SubmissionError,
} from 'gandi.react-form';

import type { Dispatch, FullState, GetState } from '../../../types';
import type { IValues } from '../types';

type InitialState = {|
  initialValues: any,
  success: boolean,
  updating: boolean,
|};

export const sendStart: any = createAction(
  '[SUPPORT] Submit contact support form'
);
export const sendSuccess: any = createAction(
  '[SUPPORT] Contact support form submitted'
);
export const sendFail: any = createAction('[SUPPORT] Contact support failed');
export const setContactInitialValues: any = createAction(
  '[SUPPORT] Contact support initialization'
);
export const redirectStart: any = createAction(
  '[SUPPORT] Redirect to success page'
);

const selectRaw: (state: FullState) => InitialState = state =>
  state.support.contact;
export const selectContactInitialValues: FullState => IValues = createSelector(
  selectRaw,
  contact => contact.initialValues
);

export function redirectOnSuccess(
  currentUrl: string,
  res: ?express$Response,
  category: ?string
): (dispatch: Dispatch, getState: GetState) => any | Promise<mixed> {
  return (dispatch: Dispatch, getState: GetState) => {
    if (currentUrl && res) {
      dispatch(redirectStart());
      const selectLayoutUrls = selectFromConfig<{ [string]: string }>(
        'app.layout.urls',
        {}
      );
      const state = getState();

      let redirSuccessUrl = superpathjoin(
        selectLayoutUrls(state).support,
        localeSelector(state),
        selectCurrentId(state),
        'success'
      );
      if (category) {
        redirSuccessUrl += `?${category}`;
      }

      res.redirect(redirSuccessUrl);
      return Promise.reject();
    }
    return dispatch(sendSuccess());
  };
}

type ValuesProps = {
  blocked?: string,
  category: string,
  firstname: string,
  lastname: string,
  message: string,
  orgname: string,
  params: {
    'application used'?: string,
    domain?: string,
    'error message'?: string,
    'error start_date'?: string,
    'error type'?: string,
    handle?: string,
    ip?: string,
    phone?: string,
  },
  reply_to: string,
  reseller: boolean,
  sharing_id: string,
  sub_category: string,
  username: string,
};

function computeSubject(values: ValuesProps) {
  let subject: ?string = '';

  if (values.category.match(/-contact$/)) {
    return values.orgname;
  }

  if (!values.params) {
    return subject;
  }

  subject = values.params.domain || values.params.ip;

  return subject;
}

function isPublic<T: string>([key]: [T, any]): boolean %checks {
  return !key.startsWith('_');
}

// values and backUrl are API parameters;
// currentUrl and res are optional and used to redirect to the success page server-side.
export function submitContactSupportForm(
  data: IValues,
  userAgent: string,
  backUrl: string,
  currentUrl: string = '',
  country: ?string = undefined,
  res: ?express$Response = undefined
): (dispatch: Dispatch, getState: GetState) => any {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(sendStart());

    // Remove all fields starting with _
    // $FlowFixMe[incompatible-indexer]
    const values: ValuesProps = Object.fromEntries(
      // $FlowFixMe[incompatible-indexer]
      // $FlowFixMe[incompatible-type]
      Object.entries(data).filter(isPublic)
    );

    // sanitize params
    if (values.params) {
      values.params = Object.keys(values.params).reduce(
        (old, current) => ({
          ...old,
          [current.replace('_', ' ')]: values.params[current],
        }),
        {}
      );
    }

    const tags = [];
    if (values.blocked === 'true') {
      tags.push('BLOCKED');
    }
    if (values.reseller === true) {
      tags.push('pro');
    }

    const params = {
      ...values,
      lang: localeSelector(getState()),
      subject: computeSubject(values),
      referrer_url: values.category === 'my-rights' ? undefined : backUrl,
      params: {
        ...values.params,
        userAgent: values.category === 'my-rights' ? undefined : userAgent,
        // $FlowFixMe[unnecessary-optional-chain] - not sure if params is always undeclared
        'error message': values.params?.['error message']
          ? `${values.params['error message']}\n`
          : undefined,
      },
      tags,
      country,
    };

    return dispatch(
      POST('/support/notifications', params, {
        on2XX: () =>
          dispatch(redirectOnSuccess(currentUrl, res, values.category)),
        on4XX: (response: any) => {
          const fieldErrors = mapErrors(response.data);

          // Server-side only:
          if (res) {
            dispatch(formActions.stopSubmit('ContactSupportForm', fieldErrors));
          }

          dispatch(sendFail());
          throw new SubmissionError(fieldErrors);
        },
        not2XX: (response: FetchResponse<>) => {
          if (!response.data || !response.data.errors) {
            return Promise.reject(dispatch(sendFail()));
          }
          return Promise.resolve(); // already handled in on4XX
        },
      })
    );
  };
}

const initialState: InitialState = {
  initialValues: undefined,
  success: false,
  updating: false,
};

export default (createReducer(
  {
    [sendStart]: state => ({
      ...state,
      updating: true,
    }),
    [sendSuccess]: state => ({
      ...state,
      success: true,
      updating: false,
    }),
    [sendFail]: state => ({
      ...state,
      success: false,
      updating: false,
    }),
    [setContactInitialValues]: (state, payload) => ({
      ...state,
      initialValues: state.initialValues || payload,
    }),
  },
  initialState
): any);
